import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  User, Save, Camera, Mail, MapPin, CreditCard, Users, ExternalLink,
  ShieldCheck, ArrowUpRight, ChevronRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES_LIST = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const TIER_LABELS = {
  plus: 'Plus',
  pro: 'Pro',
  max: 'Max',
  elite: 'Elite',
  free: 'Free',
};

/**
 * Settings page for parent users. Intentionally narrower than the student
 * profile, parents don't need academic info, GPA, interests, skills, etc.
 * Focused on: account basics, billing & subscription, and family management.
 */
export default function ParentSettingsPage() {
  const { user, getAuthHeader, updateUser } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [childCount, setChildCount] = useState(0);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    state: user?.state || '',
    city: user?.city || '',
    zip_code: user?.zip_code || '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bs, kids] = await Promise.all([
          axios.get(`${API}/billing/status`, getAuthHeader()).catch(() => null),
          axios.get(`${API}/family/children`, getAuthHeader()).catch(() => null),
        ]);
        if (cancelled) return;
        if (bs?.data) setBillingStatus(bs.data);
        const list = kids?.data?.children || kids?.data || [];
        setChildCount(Array.isArray(list) ? list.length : 0);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload/profile-photo`, fd, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      if (r.data?.url) {
        updateUser({ ...user, picture: r.data.url });
        toast.success('Photo updated!');
      }
    } catch { toast.error('Failed to upload photo'); }
    finally { setUploadingPhoto(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        full_name: formData.full_name || undefined,
        state: formData.state || undefined,
        city: formData.city || undefined,
        zip_code: formData.zip_code || undefined,
      };
      const r = await axios.put(`${API}/profile`, payload, getAuthHeader());
      updateUser(r.data || { ...user, ...payload });
      toast.success('Saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const r = await axios.post(`${API}/billing/portal`, {}, getAuthHeader());
      if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not open billing portal';
      toast.error(msg);
      // If no active subscription, send them to upgrade
      if (msg.toLowerCase().includes('no active subscription')) {
        navigate('/app/billing');
      }
    } finally {
      setOpeningPortal(false);
    }
  };

  const tier = billingStatus?.subscription_tier || user?.subscription_tier || 'free';
  const tierLabel = TIER_LABELS[tier] || tier;
  const tierInfo = billingStatus?.tier_info || null;

  return (
    <AppLayout>
      <PageHeader
        title="Settings"
        description="Manage your account, subscription, and family"
        action={
          <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-parent-settings">
            {saving ? <LoadingSpinner size="sm" /> : <Save className="w-5 h-5 mr-2" />} Save Changes
          </Button>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">

          {/* Account basics, slimmed down for parents */}
          <GlassCard>
            <h2 className="font-outfit text-lg font-semibold text-white mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" /> Account
            </h2>
            <div className="flex items-center gap-6 mb-6 pb-6 border-b border-slate-700/60">
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center border-2 border-slate-700">
                  {user?.picture
                    ? <img src={user.picture} alt="Profile" className="w-full h-full object-cover" />
                    : <User className="w-8 h-8 text-slate-300" />}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center text-white transition-colors"
                  data-testid="parent-upload-photo"
                >
                  {uploadingPhoto ? <LoadingSpinner size="sm" /> : <Camera className="w-3.5 h-3.5" />}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-300">Profile photo</p>
                <p className="text-xs text-slate-300 mt-1">PNG or JPG, up to 5MB</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-slate-300">Full Name</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="input-dark"
                  data-testid="parent-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input value={user?.email || ''} disabled className="input-dark opacity-60" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">State</Label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2.5"
                  data-testid="parent-state"
                >
                  <option value="">Select your state</option>
                  {US_STATES_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">City</Label>
                <Input
                  placeholder="Your city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input-dark"
                  data-testid="parent-city"
                />
              </div>
            </div>
          </GlassCard>

          {/* Billing & Subscription */}
          <GlassCard data-testid="parent-billing-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-outfit text-lg font-semibold text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" /> Billing & Subscription
              </h2>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                Current: {tierLabel}
              </span>
            </div>

            {tierInfo && (
              <div className="rounded-xl bg-slate-800/40 border border-slate-700/40 p-4 mb-4">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-2xl font-semibold text-white">${tierInfo.price_monthly}</span>
                  <span className="text-sm text-slate-300">/ month</span>
                  {tierInfo.price_annual && (
                    <span className="ml-3 text-xs text-slate-300">
                      or ${tierInfo.price_annual}/year (save ${(tierInfo.price_monthly * 12) - tierInfo.price_annual})
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-300">
                  {tierInfo.max_students > 0 && (
                    <>Includes {tierInfo.max_students} child · </>
                  )}
                  {tierInfo.extra_child_price && (
                    <>+${tierInfo.extra_child_price}/mo per additional child</>
                  )}
                </p>
              </div>
            )}

            <p className="text-sm text-slate-300 mb-4 leading-relaxed">
              Use the secure billing portal to upgrade or downgrade your plan, pause your subscription, update your payment method, or cancel, all in one place.
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              <Button
                onClick={openBillingPortal}
                disabled={openingPortal}
                data-testid="parent-billing-portal"
                className="w-full justify-center bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {openingPortal ? <LoadingSpinner size="sm" /> : (
                  <>
                    Manage subscription
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              <Button
                onClick={() => navigate('/app/billing')}
                variant="outline"
                data-testid="parent-billing-upgrade"
                className="w-full justify-center"
              >
                <ArrowUpRight className="w-4 h-4 mr-2" />
                Compare plans
              </Button>
            </div>

            <div className="mt-5 grid sm:grid-cols-3 gap-2 text-xs text-slate-300">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>Pause subscription</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>Switch billing period</span>
              </div>
            </div>
          </GlassCard>

          {/* Family management */}
          <GlassCard data-testid="parent-family-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-outfit text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" /> Family
              </h2>
              <span className="text-xs text-slate-300">
                {childCount} child{childCount === 1 ? '' : 'ren'} linked
              </span>
            </div>
            <p className="text-sm text-slate-300 mb-4 leading-relaxed">
              Add another child, send pending invites, or unlink an account from the Family page.
            </p>
            <Button
              onClick={() => navigate('/app/family')}
              data-testid="parent-go-to-family"
              className="w-full sm:w-auto justify-center bg-purple-600 hover:bg-purple-500 text-white"
            >
              <Users className="w-4 h-4 mr-2" />
              Manage family
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </GlassCard>
        </div>

        {/* Right rail */}
        <div className="space-y-6">
          <GlassCard>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-3">
                {user?.picture
                  ? <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                  : <User className="w-8 h-8 text-slate-300" />}
              </div>
              <h3 className="font-outfit text-lg font-semibold text-white">{user?.full_name}</h3>
              <p className="text-slate-300 text-sm">{user?.email}</p>
              <span className="mt-3 px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">
                Parent
              </span>
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-white font-semibold mb-3">Account details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3 text-slate-300">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{user?.email}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <CreditCard className="w-4 h-4 flex-shrink-0" />
                <span>{tierLabel}</span>
              </div>
              {formData.state && (
                <div className="flex items-center gap-3 text-slate-300">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span>{formData.city ? `${formData.city}, ` : ''}{formData.state}</span>
                </div>
              )}
            </div>
          </GlassCard>

          <GlassCard className="bg-gradient-to-br from-purple-600/10 to-blue-600/10 border-purple-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-purple-300" />
              <h3 className="text-purple-200 font-semibold text-sm">Help your child go further</h3>
            </div>
            <p className="text-slate-300 text-xs leading-relaxed">
              Visit your child's Roadmap or FuturePath Report to see exactly where they are and where they're headed.
            </p>
          </GlassCard>
        </div>
      </div>
    </AppLayout>
  );
}
